import { DisabledPropertyName } from '@microsoft/applicationinsights-common';
import { FC, ReactNode } from 'react';
import { cacheExchange, createClient, fetchExchange, Provider } from 'urql';
import { dependencyReportingExchange } from '../../../helpers/AppInsights';
import { useAuthenticationService } from '../../authentication/authentication-service';
import { useVariable } from '../../backstage/BackstageProvider';
import { LoggerLevel, useLogger } from '../../logging/Logger';

export const URQLProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { log } = useLogger();
  const apiUrl = useVariable('apiUrl');

  const client = createClient({
    url: apiUrl!,
    suspense: true,
    exchanges: [cacheExchange, dependencyReportingExchange, fetchExchange],
    fetchOptions: () => {
      const authService = useAuthenticationService.getState();
      const user = authService.user;
      const tenantId = authService.getEffectiveTenantId();

      const controller = new AbortController();

      const requestOptions: RequestInit = {
        headers: {
          authorization: user?.access_token ? `Bearer ${user.access_token}` : '',
          'tenant-id': tenantId ?? '',
          [DisabledPropertyName]: 'true'
        },
        signal: controller.signal
      };

      // If for whatever reason the access token is still expired at this point then abort the current request and renew the access token via signInRedirection
      if (user?.expired) {
        log('Attempt to make a request with an expired token', LoggerLevel.Warn, true);
        setTimeout(() => {
          controller.abort();
          authService.userManager.then(userManager => {
            userManager.signinRedirect();
          });
        }, 0);
      }

      return requestOptions;
    }
  });

  return <Provider value={client}>{children}</Provider>;
};

export const handleError = (error: Error) => {
  error.stack = new Error().stack;
  throw error;
};
